import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ChevronLeft, ChevronRight, Bookmark, BookmarkCheck, Highlighter,
  ListTree, Type, CheckCircle2, Lock, ExternalLink, BookOpen, Network,
} from 'lucide-react';
import publicApi from '../lib/api';
import { privateStudyApi } from '../lib/studyApi';
import ContentBlocks, { DeepDive } from '../components/ContentBlocks';
import SourceAttribution from '../components/SourceAttribution';
import { Button } from '../components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '../components/ui/sheet';
import { getReaderPriorityPresentation } from '../lib/readerPresentation';
import { useAuth } from '../context/AuthContext';

const SIZES = ['text-base', 'text-lg', 'text-xl'];

function Watermark({ email, code }) {
  const text = `${email} · ${code}`;
  return (
    <div data-testid="reader-watermark" aria-hidden
      className="pointer-events-none fixed inset-0 z-40 overflow-hidden select-none">
      <div className="absolute -inset-1/2 flex flex-wrap content-around justify-around -rotate-[24deg] opacity-[0.045]">
        {Array.from({ length: 48 }).map((_, i) => (
          <span key={i} className="text-sm font-semibold text-slate-900 whitespace-nowrap m-10 mix-blend-multiply">{text}</span>
        ))}
      </div>
    </div>
  );
}

export default function Reader({ preview = false }) {
  const { user } = useAuth();
  const isDemo = user?.role === 'PARTNER_DEMO';
  const api = preview ? privateStudyApi : publicApi;
  const routePrefix = preview ? '/admin/preview' : '';
  const { contestId, sectionId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [tree, setTree] = useState(null);
  const [completed, setCompleted] = useState(new Set());
  const [sizeIdx, setSizeIdx] = useState(1);
  const [forbidden, setForbidden] = useState(false);
  const [highlights, setHighlights] = useState([]);
  const [graph, setGraph] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const media = window.matchMedia('(max-width: 639px)');
    const update = () => setIsMobile(media.matches);
    update(); media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);

  const loadIndex = useCallback(async () => {
    try {
      const { data } = await api.get(`/library/${contestId}`);
      setTree(data.index.tree);
      setCompleted(new Set(data.completed_section_ids));
      return data;
    } catch { return null; }
  }, [contestId, api]);

  useEffect(() => {
    const boot = async () => {
      let sid = sectionId;
      if (!sid) {
        const lib = await loadIndex();
        if (!lib) { setForbidden(true); return; }
        sid = lib.stats.last_section_id
          || lib.index.tree[0]?.chapters[0]?.sections[0]?.section_id;
        if (!sid) return;
        navigate(`${routePrefix}/reader/${contestId}/${sid}`, { replace: true });
        return;
      }
      loadIndex();
      try {
        const { data } = await api.get(`/reader/${contestId}/${sid}`);
        setData(data);
        setHighlights(data.highlights || []);
        setForbidden(false);
        api.get(`/learning-graph/${contestId}/sections/${sid}`).then(({ data: graphData }) => setGraph(graphData)).catch(() => setGraph(null));
      } catch (e) {
        if (e.response?.status === 403) setForbidden(true);
        else toast.error('Errore nel caricamento della sezione');
      }
    };
    setData(null);
    boot();
  }, [contestId, sectionId, navigate, loadIndex, api, routePrefix]);

  const toggleBookmark = async () => {
    const { data: res } = await api.post('/bookmarks', { contest_id: contestId, section_id: sectionId });
    setData((d) => ({ ...d, bookmarked: res.bookmarked }));
    toast.success(res.bookmarked ? 'Segnalibro aggiunto' : 'Segnalibro rimosso');
  };

  const toggleComplete = async () => {
    const next = !data.completed;
    await api.post('/progress', { contest_id: contestId, section_id: sectionId, completed: next });
    setData((d) => ({ ...d, completed: next }));
    setCompleted((prev) => {
      const s = new Set(prev);
      next ? s.add(sectionId) : s.delete(sectionId);
      return s;
    });
    toast.success(next ? 'Sezione completata' : 'Segnata come da completare');
  };

  const saveHighlight = async () => {
    const text = window.getSelection()?.toString().trim();
    if (!text) { toast.info('Seleziona prima il testo da evidenziare'); return; }
    const { data: res } = await api.post('/highlights', { contest_id: contestId, section_id: sectionId, text });
    setHighlights((h) => [...h, { highlight_id: res.highlight_id, text }]);
    toast.success('Evidenziazione salvata');
  };

  if (forbidden) {
    return (
      <div className="max-w-xl mx-auto px-4 py-24 text-center" data-testid="reader-locked">
        <Lock className="mx-auto text-slate-300 mb-4" size={44} strokeWidth={1.5} />
        <h1 className="font-display text-2xl font-semibold text-slate-900 mb-3">Contenuto riservato</h1>
        <p className="text-slate-500 mb-8">Acquista il pacchetto per accedere a tutte le sezioni di studio.</p>
        <Button onClick={() => navigate(`/concorsi/${contestId}`)} data-testid="reader-goto-purchase"
          className="rounded-full bg-brand hover:bg-brand-hover text-white px-7">
          Vai al concorso
        </Button>
      </div>
    );
  }

  if (!data) {
    return <div className="max-w-3xl mx-auto px-4 py-16"><div className="h-96 rounded-2xl bg-slate-100 animate-pulse" /></div>;
  }

  const blockCopy = (e) => e.preventDefault();
  const allSections = tree?.flatMap((m) => m.chapters.flatMap((c) => c.sections)) || [];
  const progressPercent = allSections.length ? Math.round((completed.size / allSections.length) * 100) : 0;
  const sourceById = Object.fromEntries((graph?.sources || []).map((s) => [s.source_id, s]));
  const priorityPresentation = getReaderPriorityPresentation(data.section.learning_priority);
  const sectionContent = <ContentBlocks blocks={data.section.blocks} editorialBlocks={data.section.editorial_blocks} sources={graph?.sources || []} sizeClass={SIZES[sizeIdx]} onMapNodeSelect={setSelectedNode} onSectionSelect={(id) => navigate(`${routePrefix}/reader/${contestId}/${id}`)} />;

  return (
    <div className="reader-protected" data-testid="reader-page"
      onCopy={blockCopy} onCut={blockCopy} onContextMenu={blockCopy}>
      {preview && <div role="status" className="bg-amber-100 p-3 text-center text-sm">Anteprima privata admin · Progressi di collaudo separati · Nessun acquisto</div>}
      {isDemo && <div role="status" className="border-b border-indigo-200 bg-indigo-50 px-4 py-3 text-center text-sm" data-testid="partner-demo-banner"><strong>CORSO DIMOSTRATIVO PARTNER</strong> · Questa area serve esclusivamente a mostrare il funzionamento di ConcorsiEssentials e non costituisce accesso a un corso commerciale completo.</div>}
      <Watermark email={data.watermark.email} code={data.watermark.code} />

      {/* Header reader */}
      <div className="sticky top-[4.5rem] z-30 bg-white/90 backdrop-blur-xl border-b border-slate-200/70">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 min-h-14 flex items-center justify-between gap-3">
          <Link to={preview ? `/admin/preview/concorsi/${contestId}` : data.is_preview ? `/concorsi/${contestId}` : '/area-personale'} data-testid="reader-back"
            className="text-xs font-semibold text-slate-500 hover:text-brand inline-flex items-center gap-1">
            <ChevronLeft size={14} strokeWidth={1.5} /> {data.is_preview ? 'Torna al concorso' : 'Libreria'}
          </Link>
          <div className="min-w-0 flex-1 text-center"><p className="truncate text-xs font-semibold text-slate-600">{data.contest_title}</p><p className="mt-0.5 text-[10px] text-slate-400">{progressPercent}% completato</p></div>
          <button onClick={() => setSizeIdx((i) => (i + 1) % SIZES.length)} data-testid="reader-font-size"
            className="flex h-11 w-11 items-center justify-center rounded-full text-slate-500 hover:bg-slate-100 hover:text-brand" aria-label="Cambia dimensione testo">
            <Type size={16} strokeWidth={1.5} />
          </button>
        </div>
      </div>

      <article className="max-w-[780px] mx-auto px-5 sm:px-8 py-10 sm:py-14 select-none">
        {data.is_preview && (
          <div className="mb-8 bg-gold/10 border border-gold/30 rounded-xl px-5 py-4 text-sm text-slate-700" data-testid="preview-banner">
            Stai leggendo un'<strong>anteprima gratuita</strong>. Acquista il pacchetto per sbloccare tutte le sezioni.
          </div>
        )}
        <nav aria-label="Percorso sezione" className="mb-3 text-xs font-semibold text-slate-500"><span className="text-brand">{data.module_title}</span><span aria-hidden className="mx-2">/</span>{data.chapter_title}</nav>
        <h1 className="font-display text-3xl sm:text-4xl tracking-[-0.035em] font-bold leading-tight text-brand-dark mb-10" data-testid="reader-section-title">
          {data.section.title}
        </h1>
        {priorityPresentation.mode === 'cue' && <p className="mb-7 inline-flex rounded-full bg-blue-50 px-3 py-1 text-xs font-bold text-blue-700" data-testid="reader-editorial-cue">{priorityPresentation.label}</p>}
        {priorityPresentation.mode === 'deep-dive' ? <DeepDive>{sectionContent}</DeepDive> : sectionContent}

        {graph?.references?.length > 0 && <details className="mt-12 rounded-2xl border border-slate-200 bg-white p-5" data-testid="official-sources">
          <summary className="cursor-pointer list-none font-display font-bold text-brand-dark">Fonti e riferimenti <span className="ml-2 text-sm font-normal text-slate-400">({graph.references.length})</span></summary>
          <div className="mt-5 space-y-4">{graph.references.map((ref) => { const source = sourceById[ref.source_id]; return source ? <div key={ref.reference_id} className="rounded-xl bg-slate-50 p-4">
            <p className="font-semibold text-slate-900">{source.title}</p><p className="mt-1 text-xs text-slate-500">{[ref.article, ref.paragraph].filter(Boolean).join(' · ')}{source.issuer ? ` · ${source.issuer}` : ''}</p>
            <SourceAttribution source={source}/>
            <a href={source.official_url} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex min-h-11 items-center gap-1 text-sm font-semibold text-brand hover:underline" data-testid={`source-${source.source_id}`}>Apri la fonte <ExternalLink size={13}/><span className="sr-only"> (si apre in una nuova scheda)</span></a>
          </div> : null; })}</div>
        </details>}

        {graph?.related_sections?.length > 0 && <section className="mt-8 rounded-2xl border border-slate-200 bg-slate-50/70 p-5" data-testid="related-topics"><h2 className="font-display font-bold text-brand-dark">Vedi anche</h2><div className="mt-3 flex flex-col gap-2">{graph.related_sections.slice(0, 4).map((s) => <Link key={s.section_id} to={`${routePrefix}/reader/${contestId}/${s.section_id}`} className="inline-flex min-h-11 items-center gap-2 text-sm font-semibold text-brand hover:underline" data-testid={`related-${s.section_id}`}><BookOpen size={14}/>{s.title}<span aria-hidden>→</span></Link>)}</div></section>}

        {highlights.length > 0 && (
          <div className="mt-12 border-t border-slate-200 pt-8" data-testid="highlights-list">
            <p className="text-xs tracking-[0.1em] uppercase text-slate-500 font-semibold mb-4">Le tue evidenziazioni</p>
            <div className="space-y-3">
              {highlights.map((h) => (
                <div key={h.highlight_id} className="flex items-start justify-between gap-3 bg-yellow-200/60 rounded-sm px-3 py-2">
                  <p className="text-sm text-slate-900">“{h.text}”</p>
                  <button onClick={async () => { await api.delete(`/highlights/${h.highlight_id}`); setHighlights((x) => x.filter((y) => y.highlight_id !== h.highlight_id)); }}
                    className="text-xs text-slate-500 hover:text-rose-600 shrink-0" data-testid={`highlight-delete-${h.highlight_id}`}>
                    Rimuovi
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </article>

      {/* Toolbar flottante */}
      <div className="sticky bottom-3 z-50 mx-auto flex w-fit justify-center lg:fixed lg:bottom-5 lg:left-1/2 lg:-translate-x-1/2" data-testid="reader-toolbar">
        <div className="flex items-center gap-1 bg-white/95 backdrop-blur-xl border border-slate-200 rounded-full px-2 py-2 shadow-[0_12px_40px_-15px_rgba(7,24,63,.35)]">
          <Sheet>
            <SheetTrigger asChild>
              <button className="min-h-11 min-w-11 p-2.5 rounded-full hover:bg-slate-100 text-slate-600" data-testid="toolbar-index" aria-label="Apri indice">
                <ListTree size={18} strokeWidth={1.5} />
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="w-80 overflow-y-auto">
              <h3 className="font-display font-semibold text-lg mb-5 mt-4">Indice</h3>
              {tree?.map((m) => (
                <div key={m.module_id} className="mb-5">
                  <p className="text-xs tracking-[0.1em] uppercase text-brand font-semibold">{m.subject_name}</p>
                  <p className="font-semibold text-slate-900 text-sm mb-2">{m.title}</p>
                  {m.chapters.map((ch) => (
                    <ul key={ch.chapter_id} className="space-y-1 mb-2">
                      {ch.sections.map((s) => (
                        <li key={s.section_id}>
                          <Link to={`${routePrefix}/reader/${contestId}/${s.section_id}`}
                            data-testid={`index-section-${s.section_id}`}
                            className={`flex items-center gap-2 text-sm py-1 ${s.section_id === sectionId ? 'text-brand font-semibold' : 'text-slate-600 hover:text-brand'}`}>
                            {completed.has(s.section_id)
                              ? <CheckCircle2 size={13} strokeWidth={1.5} className="text-emerald-600 shrink-0" />
                              : <span className="w-3.5 h-3.5 rounded-full border border-slate-300 shrink-0" />}
                            {s.title}
                            {!s.free && !data.is_preview ? null : s.free ? <span className="text-[10px] text-gold font-bold">FREE</span> : null}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  ))}
                </div>
              ))}
            </SheetContent>
          </Sheet>
          <button onClick={toggleBookmark} className="min-h-11 min-w-11 p-2.5 rounded-full hover:bg-slate-100 text-slate-600" data-testid="toolbar-bookmark" aria-label={data.bookmarked ? 'Rimuovi segnalibro' : 'Aggiungi segnalibro'}>
            {data.bookmarked ? <BookmarkCheck size={18} strokeWidth={1.5} className="text-gold" /> : <Bookmark size={18} strokeWidth={1.5} />}
          </button>
          <button onClick={saveHighlight} className="min-h-11 min-w-11 p-2.5 rounded-full hover:bg-slate-100 text-slate-600" data-testid="toolbar-highlight" aria-label="Salva testo evidenziato">
            <Highlighter size={18} strokeWidth={1.5} />
          </button>
          <button onClick={toggleComplete} data-testid="toolbar-complete"
            className={`min-h-11 min-w-11 p-2.5 rounded-full ${data.completed ? 'bg-emerald-50 text-emerald-600' : 'hover:bg-slate-100 text-slate-600'}`} aria-label={data.completed ? 'Segna da completare' : 'Segna come completata'}>
            <CheckCircle2 size={18} strokeWidth={1.5} />
          </button>
          <div className="w-px h-6 bg-slate-200 mx-1" />
          <button disabled={!data.prev_section_id}
            onClick={() => data.prev_section_id && navigate(`${routePrefix}/reader/${contestId}/${data.prev_section_id}`)}
            className="p-2.5 rounded-full hover:bg-slate-100 text-slate-600 disabled:opacity-30" data-testid="toolbar-prev" aria-label="Precedente">
            <ChevronLeft size={18} strokeWidth={1.5} />
          </button>
          <button disabled={!data.next_section_id}
            onClick={() => data.next_section_id && navigate(`${routePrefix}/reader/${contestId}/${data.next_section_id}`)}
            className="p-2.5 rounded-full bg-brand text-white hover:bg-brand-hover disabled:opacity-30" data-testid="toolbar-next" aria-label="Successiva">
            <ChevronRight size={18} strokeWidth={1.5} />
          </button>
        </div>
      </div>
      <div className="h-6 lg:h-24" />
      <Sheet open={!!selectedNode} onOpenChange={(open) => !open && setSelectedNode(null)}>
        <SheetContent side={isMobile ? 'bottom' : 'right'} className="w-full sm:max-w-md max-sm:max-h-[82vh] max-sm:rounded-t-3xl" data-testid="map-node-panel" data-panel-mode={isMobile ? 'bottom-sheet' : 'side-panel'}>
          {selectedNode && <div className="mt-8"><Network className="mb-5 text-brand"/><p className="eyebrow mb-2">Nodo della mappa</p><h2 className="font-display text-2xl font-bold text-brand-dark">{selectedNode.title || selectedNode.label}</h2>{selectedNode.summary && <p className="mt-4 leading-7 text-slate-600">{selectedNode.summary}</p>}<div className="mt-7 flex flex-col gap-3">
            {selectedNode.section_id && <Button onClick={() => { setSelectedNode(null); navigate(`${routePrefix}/reader/${contestId}/${selectedNode.section_id}`); }} data-testid="node-go-section" className="rounded-full bg-brand text-white">Vai all’Essenziale</Button>}
            {selectedNode.deep_dive_section_id && <Button variant="outline" onClick={() => { setSelectedNode(null); navigate(`${routePrefix}/reader/${contestId}/${selectedNode.deep_dive_section_id}`); }} data-testid="node-go-deep-dive" className="rounded-full">Approfondisci</Button>}
            {selectedNode.quiz_id && <Button variant="outline" onClick={() => navigate(`/quiz/${contestId}?question=${selectedNode.quiz_id}`)} data-testid="node-go-quiz" className="rounded-full">Fai i quiz</Button>}
            {selectedNode.source_id && sourceById[selectedNode.source_id] && <><a href={sourceById[selectedNode.source_id].official_url} target="_blank" rel="noopener noreferrer" className="text-center text-sm font-semibold text-brand" data-testid="node-go-source">Apri la fonte <ExternalLink className="inline" size={13}/></a><SourceAttribution source={sourceById[selectedNode.source_id]}/></>}
          </div></div>}
        </SheetContent>
      </Sheet>
    </div>
  );
}
